import { useNavigate } from 'react-router-dom';
import { MapPin, BadgeCheck, Phone } from 'lucide-react';
import ShareButton from './ShareButton';
import { useT } from '../i18n/I18nContext';

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=400';

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { t } = useT();

  const handleClick = () => {
    navigate(`/prodotto/${product.id}`);
  };

  const handleShopClick = (e) => {
    e.stopPropagation();
    navigate(`/negozio/${product.negozio_id}`);
  };

  const handleTagClick = (e, tag) => {
    e.stopPropagation();
    navigate(`/search?tag=${encodeURIComponent(tag)}`);
  };

  const formatDistance = (km) => {
    if (km === null || km === undefined) return null;
    if (km < 1) {
      return `${Math.round(km * 1000)} m`;
    }
    return `${km.toFixed(1)} km`;
  };

  const calculateDiscountedPrice = () => {
    if (product.prezzo && product.sconto) {
      return product.prezzo * (1 - product.sconto / 100);
    }
    return null;
  };

  const getAvailabilityText = () => {
    if (product.disponibilita !== 'disponibile') {
      return null;
    }
    if (product.negozio_is_open === true) {
      return t('product.available_now');
    }
    if (product.negozio_is_open === false && product.negozio_next_opening) {
      const txt = product.negozio_next_opening;
      return txt.toLowerCase().startsWith('chiuso') ? txt : t('product.opens_at', { time: txt });
    }
    return t('product.available');
  };

  const discountedPrice = calculateDiscountedPrice();
  const imageUrl = product.immagini?.[0] || DEFAULT_IMAGE;
  const availabilityText = getAvailabilityText();

  return (
    <article
      onClick={handleClick}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm product-card cursor-pointer overflow-hidden"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image container */}
      <div className="relative aspect-square bg-slate-100">
        <img
          src={imageUrl}
          alt={product.nome}
          className={`absolute inset-0 w-full h-full object-cover ${
            product.disponibilita !== 'disponibile' ? 'opacity-60 grayscale' : ''
          }`}
          loading="lazy"
          onError={(e) => {
            e.target.src = DEFAULT_IMAGE;
          }}
        />

        {/* Discount badge */}
        {product.sconto && product.disponibilita === 'disponibile' && (
          <span className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold" data-testid="discount-badge">
            -{product.sconto}%
          </span>
        )}

        {/* Sold-out badge */}
        {product.disponibilita !== 'disponibile' && (
          <span
            className="absolute top-3 left-3 bg-red-600 text-white px-2.5 py-1 rounded-full text-xs font-bold tracking-wide shadow-md"
            data-testid="sold-out-badge"
          >
            {t('product.sold_out').toUpperCase()}
          </span>
        )}

        {/* Share button — bottom-right of image */}
        <div className="absolute bottom-2 right-2" onClick={(e) => e.stopPropagation()}>
          <ShareButton
            title={product.nome}
            url={`${window.location.origin}/prodotto/${product.id}`}
            testId={`product-${product.id}-share`}
            variant="icon"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Product name */}
        <h3 className="font-outfit font-semibold text-slate-800 text-sm line-clamp-2 mb-1" data-testid="product-name">
          {product.nome}
        </h3>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-2">
          {discountedPrice ? (
            <>
              <span className="text-base font-bold text-orange-500" data-testid="discounted-price">
                {discountedPrice.toFixed(2)} €
              </span>
              <span className="text-xs text-slate-400 line-through" data-testid="original-price">
                {product.prezzo.toFixed(2)} €
              </span>
            </>
          ) : product.prezzo ? (
            <span className="text-base font-bold text-slate-800" data-testid="price">
              {product.prezzo.toFixed(2)} €
            </span>
          ) : (
            <span className="text-xs text-slate-500" data-testid="no-price">
              {t('product.price_in_store')}
            </span>
          )}
        </div>

        {/* Availability badge */}
        {availabilityText && (
          <span 
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${
              product.negozio_is_open === true
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}
            data-testid="availability-badge"
          >
            {product.negozio_is_open === true && <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1" />}
            {availabilityText}
          </span>
        )}

        {/* Distance badge */}
        {product.distanza !== null && product.distanza !== undefined && (
          <span className="inline-flex items-center bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs font-medium mr-2" data-testid="distance-badge">
            <MapPin className="w-3 h-3 mr-1" />
            {formatDistance(product.distanza)}
          </span>
        )}

        {/* Shop name with verified badge */}
        {product.negozio_nome && (
          <button
            onClick={handleShopClick}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline mt-2"
            data-testid="shop-name"
          >
            {product.negozio_nome}
            {product.negozio_verified && (
              <BadgeCheck className="w-3.5 h-3.5 text-blue-500" />
            )}
          </button>
        )}

        {/* Clickable tags/hashtags */}
        {product.tag?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {product.tag.slice(0, 3).map((tag) => (
              <button
                key={tag}
                onClick={(e) => handleTagClick(e, tag)}
                className="text-xs text-blue-500 hover:text-blue-700 hover:underline"
                data-testid={`tag-${tag}`}
              >
                #{tag}
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
